import { useState } from 'react';
import {
  THEME_PALETTE_SWATCHES,
  i18n,
  themeModes,
  themePalettes,
  type ThemeMode,
  type ThemePalette,
} from '@wms/shared';
import { useAppearance } from '../lib/appearance-context.js';

const { t } = i18n;

export function AppearanceSettingsPage() {
  const { themePalette, themeMode, saving, setAppearance } = useAppearance();
  const [error, setError] = useState(false);
  const [saved, setSaved] = useState(false);

  async function update(patch: { themePalette?: ThemePalette; themeMode?: ThemeMode }) {
    setError(false);
    setSaved(false);
    try {
      await setAppearance(patch);
      setSaved(true);
    } catch {
      // The context already rolled the painted theme back to the stored one.
      setError(true);
    }
  }

  return (
    <section className="mx-auto max-w-2xl space-y-6">
      <header>
        <h2 className="text-xl font-semibold">{t('appearance.title')}</h2>
        <p className="mt-1 text-sm text-muted-foreground">{t('appearance.intro')}</p>
      </header>

      <fieldset disabled={saving} className="space-y-3">
        <legend className="text-sm font-medium">{t('appearance.palette')}</legend>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          {themePalettes.map((palette) => {
            const selected = palette === themePalette;
            return (
              <button
                key={palette}
                type="button"
                aria-pressed={selected}
                data-palette={palette}
                onClick={() => void update({ themePalette: palette })}
                className={`flex flex-col items-start gap-2 rounded-md border p-3 text-left text-sm disabled:opacity-50 ${
                  selected
                    ? 'border-primary ring-2 ring-primary font-medium'
                    : 'border-border hover:bg-muted'
                }`}
              >
                <span className="flex h-5 w-full overflow-hidden rounded" aria-hidden="true">
                  {THEME_PALETTE_SWATCHES[palette].map((color) => (
                    <span key={color} className="flex-1" style={{ backgroundColor: color }} />
                  ))}
                </span>
                <span>{t(`appearance.palette.${palette}`)}</span>
              </button>
            );
          })}
        </div>
      </fieldset>

      <fieldset disabled={saving} className="space-y-2">
        <legend className="text-sm font-medium">{t('appearance.mode')}</legend>
        <div className="flex flex-wrap gap-2">
          {themeModes.map((mode) => {
            const selected = mode === themeMode;
            return (
              <button
                key={mode}
                type="button"
                aria-pressed={selected}
                data-mode={mode}
                onClick={() => void update({ themeMode: mode })}
                className={`rounded-full border px-4 py-1.5 text-sm disabled:opacity-50 ${
                  selected
                    ? 'border-primary bg-primary text-primary-foreground font-medium'
                    : 'border-border hover:bg-muted'
                }`}
              >
                {t(`appearance.mode.${mode}`)}
              </button>
            );
          })}
        </div>
        <p className="text-xs text-muted-foreground">{t('appearance.mode.hint')}</p>
      </fieldset>

      <p className="text-sm" role="status" aria-live="polite">
        {error ? (
          <span className="text-red-600 dark:text-red-300">{t('appearance.error')}</span>
        ) : saved ? (
          <span className="text-emerald-700 dark:text-emerald-300">{t('appearance.saved')}</span>
        ) : null}
      </p>
    </section>
  );
}
